import { useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Form } from 'factorial-form'
import {
  CreatePostsGroupDocument,
  MyCommunitiesDocument,
  OtherCommunitiesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import GroupForm from 'modules/dashboard/components/GroupForm'

interface Props {
  onClose: () => void
  onSuccessClose?: (groupId: string) => void
}

function NewGroup({ onClose, onSuccessClose }: Props) {
  const [running, setRunning] = useState(false)
  const { company } = useCurrentSession()
  const [createPostsGroup] = useMutation(CreatePostsGroupDocument, {
    extractorFn: (data) => data.postsMutations.createPostsGroup,
  })

  const form = useRef(
    new Form(
      {
        title: null,
        description: null,
      },
      {
        title: 'string',
        description: 'string',
      }
    )
  ).current

  const handleSubmit = async () => {
    setRunning(true)

    const result = await createPostsGroup({
      variables: {
        companyId: company.id,
        title: form.get('title').value,
        description: form.get('description').value,
      },
      refetchQueries: [{ query: MyCommunitiesDocument }, { query: OtherCommunitiesDocument }],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, (_errors) => {})

    setRunning(false)

    if (handled.success) {
      const groupId = handled.data?.group?.id
      if (groupId && onSuccessClose) {
        onSuccessClose(groupId)
      } else {
        onClose()
      }
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.forbidden'),
        time: 2000,
      })
      onClose()
    }
  }

  const disabled = !form.get('title').value || !form.get('description').value

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('dashboard.groups.new.title')}
      width="sm"
      description={i18n.t('dashboard.groups.new.description')}
      primaryAction={{
        label: i18n.t('dashboard.groups.new.title'),
        onClick: handleSubmit,
        disabled: disabled || running,
        loading: running,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: running,
      }}
    >
      <Box paddingRight="s16">
        <GroupForm form={form} />
      </Box>
    </F0Dialog>
  )
}

export default observer(NewGroup)
